import { useAtom, useAtomValue } from 'jotai';
import { Checkbox } from '@carbon/react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from 'components/accordion/accordion';
import { layoutSettingsAtom, selectAllStateAtom } from './atoms';
import { useEval3Page } from './context';
import { ChartsVisualsSection } from './custom-chart/charts-visuals-section';
import { DatasetFilterAccordion } from './dataset-filter-accordion';
import { SpatialFilterControls } from './spatial-filter-controls';
import { useDatasetCounts } from './hooks';
import type { LayoutSectionKey } from './types';
import styles from './page-controls-panel.module.scss';

/**
 * The three P2 layout toggles in mock display order (mock §4a), each mapping a
 * `layoutSettingsAtom` key to its checkbox label.
 */
const LAYOUT_TOGGLES: { key: LayoutSectionKey; label: string }[] = [
  { key: 'executiveSummary', label: 'Executive Summary' },
  { key: 'map', label: 'Map' },
  { key: 'slotAllocationsTable', label: 'Property Allocations Table' },
  // Output sections (P5+) — listed after the P2 surfaces, per mock §4a.
  { key: 'packageOverview', label: 'Package Overview' },
  { key: 'cashflowsByMonth', label: 'Cashflows by Month' },
  { key: 'slotCount', label: 'Reserves Category Evolution' },
  { key: 'productionVolumes', label: 'Production Volumes' },
  { key: 'cashFlow', label: 'Cash Flow' },
  { key: 'outputAggregation', label: 'Output Aggregation' },
  { key: 'chartsVisuals', label: 'Charts & Visuals' },
  { key: 'interestModeling', label: 'Interest Modeling' },
];

/**
 * Left Page Controls panel for the Eval 3 master-data page. A stack of in-house
 * `components/accordion` sections: a working **Layout Settings** section (tri-state
 * "Select all" over the three P2 surface toggles, bound to `layoutSettingsAtom`), a
 * **Global Map Filter** section (`SpatialFilterControls` — the spatial scope mode),
 * and one **Assets / Properties / Units** accordion each with its Filters section
 * (`DatasetFilterAccordion`). All three titles carry a distinct `(N)` count sourced
 * from the filter- + scope-aware `executive_summary` (`useDatasetCounts`). A final
 * **Charts & Visuals** section (`ChartsVisualsSection`, which owns its own accordion)
 * covers the work-area chart list end-to-end (Add via a catalog picker, Edit rename,
 * visibility, Show All).
 *
 * @return {JSX.Element} The Page Controls panel.
 */
export function PageControlsPanel() {
  const [layout, setLayout] = useAtom(layoutSettingsAtom);
  const selectAllState = useAtomValue(selectAllStateAtom);
  // Business-heavy container: read the page identity once and push it down to the
  // accordions (thin) as props + into the counts hook as params.
  const { packageId, calculationId } = useEval3Page();

  // Accordion counts (mock §4), sourced from the filter- + scope-aware
  // executive_summary (one fetch shared with the Executive Summary panel). Units
  // uses the BE's unitCount; Properties is the whole property population
  // (matchedSlotCount = wells + LTD).
  const {
    assets: assetsCount,
    units: unitsCount,
    properties: propertiesCount,
  } = useDatasetCounts(packageId, calculationId);

  /**
   * Sets every layout section to `checked` — the "Select all" toggle action.
   *
   * @param {boolean} checked - The next value applied to all sections.
   * @return {void}
   */
  const handleSelectAll = (checked: boolean) => {
    setLayout(
      Object.fromEntries(
        LAYOUT_TOGGLES.map(({ key }) => [key, checked])
      ) as Record<LayoutSectionKey, boolean>
    );
  };

  /**
   * Flips one layout section's visibility, preserving the others.
   *
   * @param {LayoutSectionKey} key - The section to toggle.
   * @param {boolean} checked - The next value for that section.
   * @return {void}
   */
  const handleToggle = (key: LayoutSectionKey, checked: boolean) => {
    setLayout((current) => ({ ...current, [key]: checked }));
  };

  /**
   * The styled accordion title node passed to `AccordionSummary` (its own element
   * so the title carries our typography, not the summary's default weight).
   *
   * @param {string} label - The accordion section label.
   * @return {JSX.Element} The styled title node.
   */
  const accordionTitle = (label: string) => (
    <span className={styles.accordionTitle}>{label}</span>
  );

  return (
    <aside className={styles.panel}>
      {/* The eval-name parenthetical lands with the workspace/eval nav (P3). */}
      <h3 className={styles.regionHeader}>Page controls</h3>
      <div className={styles.accordions}>
        <Accordion className={styles.accordion} defaultExpanded>
          <AccordionSummary>
            {accordionTitle('Layout Settings')}
          </AccordionSummary>
          <AccordionDetails>
            {/* No visible <legend> (the accordion title labels the section), so
                name the checkbox group for assistive tech via aria-label. */}
            <fieldset
              className={styles.sectionToggleList}
              aria-label="Layout settings"
            >
              <Checkbox
                id="eval3-layout-select-all"
                labelText="Select all"
                checked={selectAllState === 'checked'}
                indeterminate={selectAllState === 'indeterminate'}
                onChange={(_evt, { checked }: { checked: boolean }) =>
                  handleSelectAll(checked)
                }
              />
              {LAYOUT_TOGGLES.map(({ key, label }) => (
                <Checkbox
                  key={key}
                  id={`eval3-layout-${key}`}
                  labelText={label}
                  checked={layout[key]}
                  onChange={(_evt, { checked }: { checked: boolean }) =>
                    handleToggle(key, checked)
                  }
                />
              ))}
            </fieldset>
          </AccordionDetails>
        </Accordion>

        <Accordion className={styles.accordion}>
          <AccordionSummary>
            {accordionTitle('Global Map Filter')}
          </AccordionSummary>
          <AccordionDetails>
            <SpatialFilterControls />
          </AccordionDetails>
        </Accordion>

        <DatasetFilterAccordion
          dataset="assets"
          packageId={packageId}
          calculationId={calculationId}
          count={assetsCount}
        />
        <DatasetFilterAccordion
          dataset="wells"
          packageId={packageId}
          calculationId={calculationId}
          count={propertiesCount}
        />
        <DatasetFilterAccordion
          dataset="units"
          packageId={packageId}
          calculationId={calculationId}
          count={unitsCount}
        />
        <ChartsVisualsSection />
      </div>
    </aside>
  );
}
